import axios from 'axios';
import store from '@/store';
import router from '@/router';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Endpoints, Mutations, Actions, OverlayMods } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';
import FacilityData from '@/ts/FacilityData';
import FacilityPhotoData from '@/ts/FacilityPhotoData';
import BookingData from '@/ts/BookingData';
import FacilityTypeData from '@/ts/FacilityTypeData';
import FacilityAvailablePeriodData from '@/ts/FacilityAvailablePeriodData';
import {
    SharedFacilitiesResponse,
    FacilitiesBookingsResponse,
    FacilityPhotosResponse,
    AddFacilityBookingRequest,
} from '@/ts/HococoInterfaces';
import FacilityUnavailabilityData from '@/ts/FacilityUnavailabilityData';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';
import FacilityBlockConfigData from '@/ts/FacilityBlockConfigData';
import { NotifyHelpers } from '@/ts/helpers/NotifyHelpers';

const moment = extendMoment(Moment);

@Module({name: 'SharedFacilities', namespaced: true})
export default class SharedFacilities extends VuexModule {
    public facilityDetailsOverlayVisibility: boolean = false;
    public bookFacilityOverlayVisibility: boolean = false;
    public bookedFacilityOverlayVisibility: boolean = false;
    public bookFacilityTimeOverlayVisibility: boolean = false;
    public bookFacilityDateOverlayVisibility: boolean = false;
    public bookedFacilityOverlayMode: string = '';
    public facilityBookingStartTime: string = '';
    public facilityBookingEndTime: string = '';
    public facilityBookingDuration: string = '';
    public facilityBookingDate: string|null = null;
    public facilityBookingCost: number = 0; 
    public facilityPageDefaultTab: number|null = null;
    public facilityFormMode: string = 'calendar_all';
    public adminCalendarSelectedDate: string = '';
    public facilityAllTypes: any = [];
    public projectAllFacilities: any[] = [];
    public activeFacilityPhotos: any[] = [];
    public activeFacilityBookings: any[] = [];
    public activeAdminFacility: any = null;
    public activeFacilitySelectedDateBookings: any[] = [];
    public addAdminFacilityId: number = 0;
    public facilityForUpdate: FacilityData = null;
    public facilityPhotosForUpdate: any[] = [];
    public userSharedFacilities: FacilityData[] = [];
    public userFacilitiesBookings: BookingData[] = [];
    public userSelectedBooking: BookingData = null;
    public userSelectedFacility: FacilityData = null;
    public userBookingRequestsStatus: boolean = false;
    public sharedFacilityPhotos: FacilityPhotoData[] = [];
    public sharedFacilityUnavailability: FacilityUnavailabilityData[] = [];
    public sharedFacilityUnavailabilityTimes: any = [];
    public facilityWatchers: any = [];
    public facilityAllWatchers: any = [];

    @Mutation
    public RESET_SHARED_FACILITIES_STORE_STATE() {
        this.facilityDetailsOverlayVisibility   = false;
        this.bookFacilityOverlayVisibility      = false;
        this.bookedFacilityOverlayVisibility    = false;
        this.bookFacilityTimeOverlayVisibility  = false;
        this.bookFacilityDateOverlayVisibility  = false;
        this.bookedFacilityOverlayMode          = '';
        this.facilityBookingStartTime           = '';
        this.facilityBookingEndTime             = '';
        this.facilityBookingDuration            = '';
        this.facilityBookingDate                = null;
        this.facilityBookingCost                 = 0;
        this.facilityPageDefaultTab             = null;
        this.facilityFormMode                   = 'calendar_all';
        this.adminCalendarSelectedDate          = '';
        this.facilityAllTypes                   = [];
        this.projectAllFacilities               = [];
        this.activeFacilityPhotos               = [];
        this.activeFacilityBookings             = [];
        this.activeAdminFacility                = null;
        this.activeFacilitySelectedDateBookings = [];
        this.addAdminFacilityId                 = 0;
        this.facilityForUpdate                  = null;
        this.facilityPhotosForUpdate            = [];
        this.userSharedFacilities               = [];
        this.userFacilitiesBookings             = [];
        this.userSelectedBooking                = null;
        this.userSelectedFacility               = null;
        this.userBookingRequestsStatus          = false;
        this.sharedFacilityPhotos               = [];
        this.sharedFacilityUnavailability       = [];
        this.sharedFacilityUnavailabilityTimes  = [];
        this.facilityWatchers                   = [];
        this.facilityAllWatchers                = [];
    }

    get getFacilityBookingCost(): number {
        return this.facilityBookingCost;
    }

    get getFacilityBookingDuration(): string {
        return this.facilityBookingDuration;
    }

    get getFacilityDetailsOverlayVisibility(): boolean {
        return this.facilityDetailsOverlayVisibility;
    }

    get getBookFacilityOverlayVisibility(): boolean {
        return this.bookFacilityOverlayVisibility;
    }

    get getBookedFacilityOverlayVisibility(): boolean {
        return this.bookedFacilityOverlayVisibility;
    }

    get getBookFacilityTimeOverlayVisibility(): boolean {
        return this.bookFacilityTimeOverlayVisibility;
    }

    get getBookFacilityDateOverlayVisibility(): boolean {
        return this.bookFacilityDateOverlayVisibility;
    }

    get getBookedFacilityOverlayMode(): string {
        return this.bookedFacilityOverlayMode;
    }

    get  getFacilityBookingTime(): string {
        return this.facilityBookingStartTime !== '' && this.facilityBookingEndTime !== ''
            ? this.facilityBookingStartTime + ' - ' + this.facilityBookingEndTime
            : '';
    }

    get getFacilityBookingStartTime(): string {
        return this.facilityBookingStartTime;
    }

    get getFacilityBookingEndTime(): string {
        return this.facilityBookingEndTime;
    }

    get getFacilityBookingDate(): string {
        return this.facilityBookingDate;
    }

    get getFacilityPageDefaultTab(): number {
        return this.facilityPageDefaultTab;
    }

    get getFacilityFormMode(): string {
        return this.facilityFormMode;
    }

    get getActiveAdminFacility(): any {
        return this.activeAdminFacility;
    }

    get getAdminCalendarSelectedDate(): string {
        return this.adminCalendarSelectedDate;
    }

    // admin
    get getFacilityAllTypes() {
        return this.facilityAllTypes;
    }

    get getProjectAllFacilities() {
        return this.projectAllFacilities;
    }

    get getFacilityForUpdate() {
        return this.facilityForUpdate;
    }

    get getFacilityPhotosForUpdate() {
        return this.facilityPhotosForUpdate;
    }

    get getActiveFacilityPhotos() {
        return this.activeFacilityPhotos;
    }

    get getActiveFacilityBookings() {
        return this.activeFacilityBookings;
    }

    get getActiveFacilitySelectedDateBookings() {
        return this.activeFacilitySelectedDateBookings;
    }

    get getAddAdminFacilityId() {
        return this.addAdminFacilityId;
    }

    get getAdminBooking() {
        return (bookingId: number) => {
            return this.activeFacilityBookings.find((booking: any) => booking.id === bookingId);
        };
    }
// <--- admin

    get getUserSharedFacilities(): FacilityData[] {
        return this.userSharedFacilities;
    }

    get getUserBooking() {
        return (bookingId: number) => {
            return this.userFacilitiesBookings.find((booking: any) => booking.id === bookingId);
        };
    }

    get getUserFacilitiesBookings(): BookingData[] {
        return this.userFacilitiesBookings;
    }

    get getSharedFacilityPhotos(): FacilityPhotoData[] {
        return this.sharedFacilityPhotos;
    }

    get getUserSelectedBooking(): BookingData {
        return this.userSelectedBooking;
    }

    get getUserSelectedFacility(): FacilityData {
        return this.userSelectedFacility;
    }

    get getUserBookingRequestsStatus(): boolean {
        return this.userBookingRequestsStatus;
    }

    get getSharedFacilityUnavailability(): FacilityUnavailabilityData[] {
        return this.sharedFacilityUnavailability;
    }

    get getSharedFacilityUnavailabilityTimes(): any {
        return this.sharedFacilityUnavailabilityTimes;
    }

    get getFacilityAllWatchers(): any {
        return this.facilityAllWatchers;
    }

    get getFacilityWatchers(): any {
        return this.facilityWatchers;
    }

    // admin
    @Action
    public FETCH_ALL_FACILITY_TYPES() {
        axios.get(Endpoints.Admin.SharedFacilities.ALL_FACILITY_TYPES)
            .then((response: any) => {
                store.commit(Mutations.SET_ALL_FACILITY_TYPES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_FACILITY_FOR_UPDATE(facilityId: number) {
        axios.get(Endpoints.Admin.SharedFacilities.SINGLE_FACILITY(facilityId))
            .then((response: any) => {
                store.commit(Mutations.SET_FACILITY_FOR_UPDATE, FacilityData.fromResponseData(response.data));
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_FACILITY_PHOTOS_FOR_UPDATE(facilityId: number) {
        axios.get(Endpoints.Admin.SharedFacilities.FACILITY_PHOTOS_FOR_UPDATE(facilityId))
            .then((response: any) => {
                store.commit(Mutations.SET_FACILITY_PHOTOS_FOR_UPDATE, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_PROJECT_ALL_FACILITIES(projectId: number) {
        axios.get(Endpoints.Admin.SharedFacilities.PROJECT_ALL_FACILITIES(projectId))
            .then((response: any) => {
                store.commit(Mutations.SET_PROJECT_ALL_FACILITIES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ACTIVE_ADMIN_FACILITY_PHOTOS(facilityId: number) {
        axios.get(Endpoints.Admin.SharedFacilities.ACTIVE_ADMIN_FACILITY_PHOTOS(facilityId))
            .then((response: any) => {
                store.commit(Mutations.SET_ACTIVE_ADMIN_FACILITY_PHOTOS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ACTIVE_ADMIN_FACILITY_BOOKINGS(facilityId: number) {
        axios.get(Endpoints.Admin.SharedFacilities.ACTIVE_ADMIN_FACILITY_BOOKINGS(facilityId))
            .then((response: any) => {
                store.commit(Mutations.SET_ADMIN_ACTIVE_FACILITY_BOOKINGS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_ADMIN_ACTIVE_FACILITY(data: any) {
        axios.delete(Endpoints.Admin.SharedFacilities.DELETE_ADMIN_ACTIVE_FACILITY(data.facilityId))
            .then((response: any) => {
                if (response.data === 'Z') {
                    NotifyHelpers.notify('facility_has_active_bookings_please_cancel_them_before_delete', 'warning');
                } else {
                    store.commit(Mutations.REMOVE_ADMIN_ACTIVE_FACILITY, data.facilityId);
                    store.commit(Mutations.RESET_ADMIN_ACTIVE_FACILITY);
                    NotifyHelpers.notify('facility_has_been_deleted', 'positive');
                }
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CANCEL_RESERVATION(data: any) {
        axios.delete(Endpoints.Admin.SharedFacilities.CANCEL_RESERVATION(data.bookingId))
            .then((response: any) => {
                store.commit(Mutations.REMOVE_FROM_ACTIVE_BOOKINGS, data.bookingId);
                NotifyHelpers.notify('booking_has_been_canceled', 'positive');
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_FACILITY(data: any) {
        axios.post(Endpoints.Admin.SharedFacilities.ADD_NEW_FACILITY(data.project), data)
            .then((response) => {
                store.commit(Mutations.SET_ADD_FACILITY_ID, response.data);
                NotifyHelpers.notify('facility_has_been_created', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_FACILITY(data: any) {
        axios.post(Endpoints.Admin.SharedFacilities.SINGLE_FACILITY(data.id), data)
            .then(() => {
                NotifyHelpers.notify('facility_has_been_updated', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_PHOTOS_TO_NEW_FACILITY(data: any) {
        axios.post(Endpoints.Admin.SharedFacilities.PHOTOS_TO_NEW_FACILITY(data.facilityId), data.requestData)
            .then(() => {
                router.push({ name: 'admin-shared-facilities' });
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_FACILITY_PHOTOS(data: any) {
        axios.post(Endpoints.Admin.SharedFacilities.UPDATE_FACILITY_PHOTOS(data.facilityId), data.requestData)
            .then((response) => {
                NotifyHelpers.notify('facility_photos_has_been_updated', 'positive');
                store.commit(Mutations.SET_FACILITY_PHOTOS_FOR_UPDATE, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_FACILITY_PHOTOS_ORDER(data: any) {
        axios.put(Endpoints.Admin.SharedFacilities.FACILITY_PHOTOS_ORDER(data.facilityId), data.dragOptions)
            .then(() => {
                NotifyHelpers.notify('facility_photos_order_has_been_updated', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CHANGE_ACTIVE_ADMIN_FACILITY_AVAILABLE(data: any) {
        axios.put(Endpoints.Admin.SharedFacilities.FACILITY_AVAILABLE(data.facilityId, data.available))
            .then((response) => {
                NotifyHelpers.notify('facility_availability_has_been_updated', 'positive');
                store.commit(Mutations.SET_ACTIVE_ADMIN_FACILITY_MASK, {facilityId: response.data.id, mask: response.data.availability_mask});
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

// <--- admin
    @Action
    public FETCH_USER_SHARED_FACILITIES() {
        axios.get(Endpoints.Booking.USER_SHARED_FACILITIES)
            .then((response: SharedFacilitiesResponse) => {
                store.commit(Mutations.RESET_USER_SHARED_FACILITIES);
                store.commit(Mutations.SET_USER_SHARED_FACILITIES, response);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_SHARED_FACILITIES_PHOTOS(facilityId: number) {
        axios.get(Endpoints.Booking.SHARED_FACILITY_PHOTOS(facilityId))
            .then((response: FacilityPhotosResponse) => {
                store.commit(Mutations.RESET_SHARED_FACILITIES_PHOTOS);
                store.commit(Mutations.SET_SHARED_FACILITIES_PHOTOS, response);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_FACILITIES_BOOKINGS() {
        axios.get(Endpoints.Booking.USER_FACILITIES_BOOKINGS)
            .then((response: FacilitiesBookingsResponse) => {
                store.commit(Mutations.RESET_USER_FACILITIES_BOOKINGS);
                store.commit(Mutations.SET_USER_FACILITIES_BOOKINGS, response);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_FACILITY_UNAVAILABILITY(data: any) {
        axios.get(Endpoints.Booking.FACILITY_UNAVAILABILITY(data.facilityId, data.date))
            .then((response) => {
                store.commit(Mutations.SET_SHARED_FACILITIES_UNAVAILABILITY, response.data);
            })
            .catch((error) => {
                store.commit(Mutations.SET_USER_BOOKING_REQUESTS_STATUS, false);

                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_USER_BOOKING(data: AddFacilityBookingRequest) {
        axios.post(Endpoints.Booking.ADD_USER_BOOKING(data.facilityId), {
            date: data.date,
            startTime: data.startTime,
            endTime: data.endTime,
            facilityId: data.facilityId,
        })
            .then((response: any) => {
                const booking = new BookingData(
                    response.data.id,
                    response.data.thumbnail,
                    response.data.name,
                    response.data.facility_id,
                    response.data.user_id,
                    response.data.user,
                    response.data.start_time,
                    response.data.end_time,
                    response.data.created_at,
                    response.data.updated_at,
                    response.data.deleted_at,
                );
                store.commit(Mutations.SET_USER_SELECTED_BOOKING, booking);
                store.commit(Mutations.SET_BOOK_FACILITY_OVERLAY_VISIBILITY, false);
                store.commit(Mutations.SET_FACILITY_DETAILS_OVERLAY_VISIBILITY, false);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CANCEL_USER_BOOKING(bookingId: number) {
        store.commit(Mutations.SET_USER_BOOKING_REQUESTS_STATUS, true);

        axios.delete(Endpoints.Booking.CANCEL_USER_BOOKING(bookingId))
            .then((response) => {
                store.commit(Mutations.DELETE_USER_BOOKING, bookingId);
                store.commit(Mutations.SET_BOOKED_FACILITY_OVERLAY_MODE, OverlayMods.PreviewBooking.deleted);
                store.commit(Mutations.SET_USER_BOOKING_REQUESTS_STATUS, false);
            })
            .catch((error) => {
                store.commit(Mutations.SET_USER_BOOKING_REQUESTS_STATUS, false);

                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_FACILITY_WATCHERS(data: any) {
        axios.post(Endpoints.Admin.SharedFacilities.FACILITY_WATCHERS, {
            search: data.search,
            watchers: data.watchers,
        })
            .then((response) => {
                store.commit(Mutations.SET_FACILITY_WATCHERS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_FACILITY_ALL_WATCHERS() {
        axios.post(Endpoints.Admin.SharedFacilities.FACILITY_WATCHERS, {
            search: '',
            watchers: [],
        })
            .then((response) => {
                store.commit(Mutations.SET_FACILITY_ALL_WATCHERS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    // admin
    @Mutation
    public SET_ALL_FACILITY_TYPES(data: any) {
        this.facilityAllTypes = data;
    }

    @Mutation
    public SET_ADD_FACILITY_ID(facilityId: number) {
        this.addAdminFacilityId = facilityId;
    }

    @Mutation
    public SET_FACILITY_FOR_UPDATE(data: any) {
        this.facilityForUpdate = data;
    }

    @Mutation
    public REMOVE_FACILITY_FOR_UPDATE() {
        this.facilityForUpdate = null;
    }

    @Mutation
    public SET_PROJECT_ALL_FACILITIES(facilities: any) {
        this.projectAllFacilities = [];

        for (const facility of facilities) {
            let block  = null;

            const type = FacilityTypeData.fromResponseData(facility.type);

            if (facility.block_config) {
                block = FacilityBlockConfigData.fromResponseData(facility.block_config);
            }

            this.projectAllFacilities.push(new FacilityData(
                facility.id,
                facility.project_id,
                facility.available,
                FacilityAvailablePeriodData.fromResponseData(facility.available_period),
                type,
                block,
                facility.name,
                facility.price,
                facility.description,
                facility.terms_and_conditions,
                facility.thumbnail,
                facility.availability_mask,
                facility.minimum_lease_time,
                facility.maximum_lease_time,
                facility.booking_option,
                facility.created_at,
                facility.updated_at,
                facility.deleted_at,
                facility.books_count,
                facility.watchers,
            ));
        }
    }

    @Mutation
    public SET_ACTIVE_ADMIN_FACILITY(facilityId: number|null): void {
        this.activeAdminFacility = facilityId ? this.projectAllFacilities.find((facility: any) => facility.id === facilityId) : null;
    }

    @Mutation
    public SET_ACTIVE_ADMIN_FACILITY_MASK(data: any): void {
        const fiacility = this.projectAllFacilities.find((facility: any) => facility.id === data.facilityId);
        fiacility.availabilityMask = data.mask;
    }

    @Mutation
    public REMOVE_UPDATED_FACILITY_PHOTO(index: number) {
        this.facilityPhotosForUpdate = this.facilityPhotosForUpdate.filter((image: File, imageIndex: number) => {
            return imageIndex !== index;
        });
    }

    @Mutation
    public SET_ACTIVE_ADMIN_FACILITY_PHOTOS(photos: any) {
        this.activeFacilityPhotos = [];
        for (const photo of photos) {
            this.activeFacilityPhotos.push(new FacilityPhotoData(
                photo.id,
                photo.order,
                photo.storage_guid,
                photo.base64,
                photo.created_at,
                photo.updated_at,
                photo.deleted_at,
            ));
        }
    }


    @Mutation
    public SET_FACILITY_PHOTOS_FOR_UPDATE(photos: any) {
        this.facilityPhotosForUpdate = [];
        for (const photo of photos) {
            this.facilityPhotosForUpdate.push(new FacilityPhotoData(
                photo.id,
                photo.order,
                photo.storage_guid,
                photo.base64,
                photo.created_at,
                photo.updated_at,
                photo.deleted_at,
            ));
        }
    }

    @Mutation
    public SET_ADMIN_ACTIVE_FACILITY_BOOKINGS(bookings: any) {
        this.activeFacilityBookings = [];
        for (const booking of bookings) {
            this.activeFacilityBookings.push(new BookingData(
                booking.id,
                booking.thumbnail,
                booking.name,
                booking.facility_id,
                booking.user_id,
                booking.user,
                booking.start_time,
                booking.end_time,
                booking.created_at,
                booking.updated_at,
                booking.deleted_at,
            ));
        }
    }

    @Mutation
    public SET_ADMIN_ACTIVE_FACILITY_SELECTED_DATE_BOOKINGS(bookings: any) {
        this.activeFacilitySelectedDateBookings = [];
        for (const booking of bookings) {
            this.activeFacilitySelectedDateBookings.push(new BookingData(
                booking.id,
                booking.facilityThumbnail,
                booking.facilityName,
                booking.facilityId,
                booking.userId,
                booking.user,
                booking.startTime,
                booking.endTime,
                booking.createdAt,
                booking.updatedAt,
                booking.deletedAt,
            ));
        }
    }

    @Mutation
    public REMOVE_ADMIN_ACTIVE_FACILITY(facilityId: number): void {
        this.projectAllFacilities = this.projectAllFacilities.filter((facility: any) => {
            return facility.id !== facilityId;
        });
    }

    @Mutation
    public REMOVE_FROM_ACTIVE_BOOKINGS(bookingId: number): void {
        this.activeFacilitySelectedDateBookings = this.activeFacilitySelectedDateBookings.filter((booking: any) => {
            return booking.id !== bookingId;
        });
        this.activeFacilityBookings = this.activeFacilityBookings.filter((booking: any) => {
            return booking.id !== bookingId;
        });
    }

    @Mutation
    public RESET_ADMIN_ACTIVE_FACILITY(): void {
        store.commit(Mutations.SET_FACILITY_FORM_MODE, 'default');
        store.commit(Mutations.SET_ACTIVE_ADMIN_FACILITY, null);
        store.commit(Mutations.SET_ACTIVE_ADMIN_FACILITY_PHOTOS, []);
        store.commit(Mutations.SET_ADMIN_ACTIVE_FACILITY_BOOKINGS, []);
    }
// <--- admin

    @Mutation
    public SET_USER_SHARED_FACILITIES(sharedFacilities: any): void {
        for (const facility of sharedFacilities.data) {
            const type = FacilityTypeData.fromResponseData(facility.type);

            let block = null;

            if (facility.block_config) {
                block = FacilityBlockConfigData.fromResponseData(facility.block_config);
            }

            this.userSharedFacilities.push(new FacilityData(
                facility.id,
                facility.project_id,
                facility.available,
                FacilityAvailablePeriodData.fromResponseData(facility.available_period),
                type,
                block,
                facility.name,
                facility.price,
                facility.description,
                facility.terms_and_conditions,
                facility.thumbnail,
                facility.availability_mask,
                facility.minimum_lease_time,
                facility.maximum_lease_time,
                facility.booking_option,
                facility.created_at,
                facility.updated_at,
                facility.deleted_at,
                facility.books_count,
                facility.watchers,
            ));
        }
    }

    @Mutation
    public SET_USER_FACILITIES_BOOKINGS(bookings: FacilitiesBookingsResponse): void {
        for (const booking of bookings.data) {
            this.userFacilitiesBookings.push(new BookingData(
                booking.id,
                booking.thumbnail,
                booking.name,
                booking.facility_id,
                booking.user_id,
                booking.user,
                booking.start_time,
                booking.end_time,
                booking.created_at,
                booking.updated_at,
                booking.deleted_at,
            ));
        }
    }

    @Mutation
    public SET_SHARED_FACILITIES_UNAVAILABILITY(data: any): void {
        const userTimeNow = new Date();

        this.sharedFacilityUnavailabilityTimes = [];
        this.sharedFacilityUnavailabilityTimes = data;

        this.sharedFacilityUnavailabilityTimes.map((block: any) => {
            const blockTime = new Date(block.block);

            if (userTimeNow.getTime() >= blockTime.getTime()) {
                block.status = 'time_passed';
            }
        });

        if (this.userSelectedFacility.requiredTimeSelect) {
            this.bookFacilityTimeOverlayVisibility = true;
        }
    }

    @Mutation
    public SET_SHARED_FACILITIES_PHOTOS(photos: FacilityPhotosResponse): void {
        for (const photo of photos.data) {
            this.sharedFacilityPhotos.push(new FacilityPhotoData(
                photo.id,
                photo.order,
                photo.storage_guid,
                photo.base64,
                photo.created_at,
                photo.updated_at,
                photo.deleted_at,
            ));
        }
    }

    @Mutation
    public SET_FACILITY_PAGE_DEFAULT_TAB(tabIndex: number): void {
        this.facilityPageDefaultTab = tabIndex;
    }

    @Mutation
    public SET_FACILITY_DETAILS_OVERLAY_VISIBILITY(visibility: boolean): void {
        this.facilityDetailsOverlayVisibility = visibility;
    }

    @Mutation
    public SET_BOOKED_FACILITY_OVERLAY_VISIBILITY(visibility: boolean): void {
        this.bookedFacilityOverlayVisibility = visibility;
    }

    @Mutation
    public SET_BOOK_FACILITY_OVERLAY_VISIBILITY(visibility: boolean): void {
        this.bookFacilityOverlayVisibility = visibility;
    }

    @Mutation
    public SET_BOOK_FACILITY_TIME_OVERLAY_VISIBILITY(visibility: boolean): void {
        this.bookFacilityTimeOverlayVisibility = visibility;
    }

    @Mutation
    public SET_BOOK_FACILITY_DATE_OVERLAY_VISIBILITY(visibility: boolean): void {
        this.bookFacilityDateOverlayVisibility = visibility;
    }

    @Mutation
    public SET_BOOKED_FACILITY_OVERLAY_MODE(mode: string): void {
        this.bookedFacilityOverlayMode = mode;
    }

    @Mutation
    public SET_FACILITY_BOOKING_TIME(times: any): void {
        this.facilityBookingStartTime = times.startTime;
        this.facilityBookingEndTime   = times.endTime;
    }

    @Mutation
    public SET_FACILITY_BOOKING_DATE(date: string): void {
        this.facilityBookingDate = date;
    }

    @Mutation
    public SET_FACILITY_FORM_MODE(mode: string): void {
        this.facilityFormMode = mode;
    }

    @Mutation
    public DELETE_USER_BOOKING(bookingId: number) {
        const booking = this.userFacilitiesBookings.find((bk: any) => bk.id === bookingId);
        booking.deletedAt = moment().format('YYYY-MM-DD HH:mm:ss');
    }

    @Mutation
    public SET_USER_SELECTED_BOOKING(booking: BookingData|null): void {
        this.userSelectedBooking = booking;
    }

    @Mutation
    public SET_USER_SELECTED_FACILITY(facility: FacilityData|null): void {
        this.userSelectedFacility = facility;
    }

    @Mutation
    public SET_USER_BOOKING_REQUESTS_STATUS(status: boolean): void {
        this.userBookingRequestsStatus = status;
    }

    @Mutation
    public SET_ADMIN_CALENDAR_SELECTED_DATE(date: string): void {
        this.adminCalendarSelectedDate = date;
    }

    @Mutation
    public RESET_FACILITY_BOOKING_TIME(): void {
        this.facilityBookingStartTime = '';
        this.facilityBookingEndTime   = '';
    }

    @Mutation
    public RESET_USER_SHARED_FACILITIES(): void {
        this.userSharedFacilities = [];
    }

    @Mutation
    public RESET_USER_FACILITIES_BOOKINGS(): void {
        this.userFacilitiesBookings = [];
    }

    @Mutation
    public RESET_FACILITY_BOOKING_DATE(): void {
        this.facilityBookingDate = null;
    }

    @Mutation
    public RESET_SHARED_FACILITIES_PHOTOS(): void {
        this.sharedFacilityPhotos = [];
    }

    @Mutation
    public RESET_SHARED_FACILITY_UNAVAILABILITY(): void {
        this.sharedFacilityUnavailability = [];
    }

    @Mutation
    public UPDATE_MAX_BOOKABLE_BLOCKS(value: number): void {
        this.facilityForUpdate.block.maxBookableBlocks = value;
    }

    @Mutation
    public UPDATE_MAX_BOOKINGS_PER_BLOCK(value: number): void {
        this.facilityForUpdate.block.maxBookingsPerBlock = value;
    }

    @Mutation
    public UPDATE_MIN_BOOKABLE_BLOCKS(value: number): void {
        this.facilityForUpdate.block.minBookableBlocks = value;
    }

    @Mutation
    public UPDATE_PRICE(value: number): void {
        this.facilityForUpdate.block.price = value;
    }

    @Mutation
    public SET_FACILITY_WATCHERS(data: any) {
        this.facilityWatchers = data;
    }

    @Mutation
    public SET_FACILITY_ALL_WATCHERS(data: any) {
        this.facilityAllWatchers = data;
    }

    @Mutation
    public SET_FACILITY_BOOKING_DURATION(duration) {
        this.facilityBookingDuration = duration;
    }

    @Mutation
    public SET_FACILITY_BOOKING_COST(cost) {
        this.facilityBookingCost = cost;
    }
}
